import { type FormEvent } from 'react'
import { Link, useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export type BlogCategoryFormData = {
  name: string
  slug: string
  description: string
  position: number
}

type Props = {
  initialData: BlogCategoryFormData
  categoryId?: string
  errors?: Partial<Record<string, string[] | string>>
}

export default function BlogCategoryForm({ initialData, categoryId, errors }: Props) {
  const { data, setData, post, patch, processing, transform } = useForm<BlogCategoryFormData>(initialData)

  function submit(e: FormEvent) {
    e.preventDefault()
    transform((d) => ({ blog_category: d }))
    if (categoryId) {
      patch(`/admin/blog_categories/${categoryId}`)
    } else {
      post('/admin/blog_categories')
    }
  }

  const errorFor = (field: string) => {
    const e = errors?.[field]
    if (!e) return null
    return Array.isArray(e) ? e.join(', ') : String(e)
  }

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-6">
      <div>
        <Link
          href="/admin/blog_categories"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to categories
        </Link>
        <h1 className="mt-2 font-serif text-3xl font-semibold tracking-tight">
          {categoryId ? 'Edit category' : 'New category'}
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Name<span className="text-destructive"> *</span>
            </Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
            />
            {errorFor('name') && <p className="text-xs text-destructive">{errorFor('name')}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={data.slug}
              onChange={(e) => setData('slug', e.target.value)}
              placeholder="auto-generated from name"
            />
            <p className="text-xs text-muted-foreground">
              /blog/category/<span className="font-mono">{data.slug || 'your-slug'}</span>
            </p>
            {errorFor('slug') && <p className="text-xs text-destructive">{errorFor('slug')}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={data.description}
              onChange={(e) => setData('description', e.target.value)}
              rows={2}
              placeholder="Shown in the category hero."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="position">Display order</Label>
            <Input
              id="position"
              type="number"
              value={data.position}
              onChange={(e) => setData('position', Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">Lower numbers appear first.</p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={processing}>
          {processing ? 'Saving…' : 'Save category'}
        </Button>
      </div>
    </form>
  )
}
